import React from 'react';
import { Table, Typography, Skeleton, Space } from 'antd';
import { useQuery } from 'react-query';
import { customerPortalService, ReceiptDetail as ReceiptDetailType } from '../../services/customerPortalService';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface ReceiptDetailInlineProps {
  token: string;
  receiptId: number;
  password: string;
}

const columns = [
  {
    title: 'Tên thuốc',
    dataIndex: 'medicine_name',
    key: 'medicine_name',
    width: 240,
    ellipsis: true,
    align: 'left' as const,
  },
  {
    title: 'Mã thuốc',
    dataIndex: 'medicine_code',
    key: 'medicine_code',
    width: 130,
    ellipsis: true,
  },
  {
    title: 'Số lô',
    dataIndex: 'lot_number',
    key: 'lot_number',
    width: 110,
    render: (text: string) => text || '—',
  },
  {
    title: 'Số lượng',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 120,
    align: 'right' as const,
    render: (quantity: number, record: any) => `${quantity} ${record.unit || ''}`.trim(),
  },
  {
    title: 'Đơn giá',
    dataIndex: 'unit_price',
    key: 'unit_price',
    width: 130,
    align: 'right' as const,
    render: (price: number) => formatCurrency(price),
  },
  {
    title: 'Thành tiền',
    key: 'total',
    width: 150,
    align: 'right' as const,
    render: (_: any, record: any) => formatCurrency(record.quantity * record.unit_price),
  },
];

export const ReceiptDetailInline: React.FC<ReceiptDetailInlineProps> = ({ token, receiptId, password }) => {
  const { data, isLoading } = useQuery<ReceiptDetailType>(
    ['receipt-detail-inline', token, receiptId, password],
    () => customerPortalService.getReceiptDetail(token, receiptId, password),
    {
      enabled: !!token && !!receiptId && !!password,
      retry: false,
    }
  );

  if (isLoading || !data) {
    return (
      <div style={{ padding: 16, background: '#f8fafc', borderRadius: 8 }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  const { voucher, items } = data;
  const totalAmount = voucher.total_amount;

  return (
    <div
      style={{
        background: '#f8fafc',
        padding: 16,
        borderRadius: 12,
        border: '1px solid #e2e8f0',
      }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} align="center">
          <div>
            <Typography.Text strong style={{ fontSize: 15 }}>
              Phiếu {voucher.voucher_number}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {voucher.created_at ? formatDate(voucher.created_at) : '—'}
            </Typography.Text>
          </div>
        </Space>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
          <div>
            <Typography.Text type="secondary">Tổng tiền</Typography.Text>
            <div style={{ fontWeight: 600 }}>{formatCurrency(totalAmount)}</div>
          </div>
          <div>
            <Typography.Text type="secondary">Số dòng sản phẩm</Typography.Text>
            <div style={{ fontWeight: 600 }}>{items.length}</div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          pagination={false}
          size="small"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <Typography.Text strong>Tổng tiền:</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Typography.Text strong style={{ color: '#0ea5e9' }}>
                    {formatCurrency(totalAmount)}
                  </Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Space>
    </div>
  );
};


